import type { InterviewStage } from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { buildAssignmentOptions } from "@/hooks/use-interviewer-assignment";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import type {
  AssignmentOption,
  InitMoveParams,
  PendingMove,
  PersonOption,
  StageInterviewersData,
} from "@/types/move-application";

export interface UseMoveApplicationOptions {
  jobId: string | undefined;
  jobTitle: string;
  jobLevel?: string;
  jobDepartment?: string;
  hiringManagerId?: string;
  hiringManagerFullName?: string;
  interviewStages?: InterviewStage[];
  stageInterviewers?: StageInterviewersData;
  isLoadingStageInterviewers?: boolean;
  people?: PersonOption[];
  onSuccess?: () => void;
  additionalInvalidateKeys?: string[][];
  /** Unified ATS management capability (admin OR recruiting OR hiring manager). */
  canManage?: boolean;
}

export function useMoveApplication(options: UseMoveApplicationOptions) {
  const {
    jobId,
    jobTitle,
    jobLevel: jobLevelOption,
    jobDepartment,
    hiringManagerId,
    hiringManagerFullName,
    stageInterviewers,
    isLoadingStageInterviewers = false,
    people,
    onSuccess,
    additionalInvalidateKeys,
    canManage = false,
  } = options;

  const { toast } = useToast();

  // Core move state
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Interviewer assignment state
  const [interviewerId, setInterviewerId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [assignmentMode, setAssignmentMode] = useState<"automatic" | "manual">("automatic");
  const [createNewRecord, setCreateNewRecord] = useState(false);

  // Email state
  const [sendEmailToCandidate, setSendEmailToCandidate] = useState(true);
  const [emailStageName, setEmailStageName] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailIntroText, setEmailIntroText] = useState("");
  const [emailExtraBody, setEmailExtraBody] = useState("");
  const [emailButtonText, setEmailButtonText] = useState("");
  const [emailClosingText, setEmailClosingText] = useState("");

  // Owner state
  const [currentStepOwnerId, setCurrentStepOwnerId] = useState("");

  // Rejection state
  const [rejectionReason, setRejectionReason] = useState("");
  const [sendRejectionFeedback, setSendRejectionFeedback] = useState(false);
  const [rejectionFeedbackSubject, setRejectionFeedbackSubject] = useState(
    "Atualização sobre seu processo seletivo",
  );
  const [rejectionFeedbackBody, setRejectionFeedbackBody] = useState("");
  const [showRejectionPreview, setShowRejectionPreview] = useState(false);

  // Offer dialog state
  const [isCreateOfferDialogOpen, setIsCreateOfferDialogOpen] = useState(false);
  const [offerDialogApplicationId, setOfferDialogApplicationId] = useState("");
  const [offerDialogApplicantName, setOfferDialogApplicantName] = useState("");

  // Hire form state
  const [hireFullName, setHireFullName] = useState("");
  const [hirePersonalEmail, setHirePersonalEmail] = useState("");
  const [hirePhone, setHirePhone] = useState("");
  const [hireEmploymentType, setHireEmploymentType] = useState<"CLT" | "PJ" | "">("PJ");
  const [hireJobTitle, setHireJobTitle] = useState("");
  const [hireJobLevel, setHireJobLevel] = useState("");
  const [hireDepartment, setHireDepartment] = useState("");
  const [hireStartDate, setHireStartDate] = useState("");
  const [hireInitialSalary, setHireInitialSalary] = useState("");

  // Email defaults effect
  const _pendingMoveId = pendingMove?.applicationId;
  const _pendingMoveStageId = pendingMove?.targetInterviewStageId;
  useEffect(() => {
    if (pendingMove?.isInterviewStage && pendingMove.stage) {
      const stageName = pendingMove.stage.stageName || "Entrevista";
      const jTitle = jobTitle || "a vaga";
      setEmailStageName(stageName);
      setEmailSubject(`Próxima etapa: ${stageName} — ${jTitle}`);
      setEmailIntroText(
        `Temos uma novidade! Você avançou no processo seletivo para ${jTitle} e queremos te convidar para a próxima etapa.`,
      );
      setEmailButtonText("Agendar Entrevista");
      setEmailClosingText("Qualquer dúvida, é só responder este email.\nBoa sorte! 🍀");
      setEmailExtraBody("");
    }
  }, [jobTitle, pendingMove?.isInterviewStage, pendingMove?.stage?.stageName, pendingMove?.stage]);

  // Move mutation
  const moveMutation = useMutation({
    mutationFn: async (params: {
      applicationId: string;
      targetColumn: string;
      targetInterviewStageId?: string | null;
      currentStepOwnerId?: string;
      forceMove?: boolean;
      createNewRecord?: boolean;
      rejectionReason?: string;
      rejectionFeedback?: string;
      rejectionFeedbackSubject?: string;
      sendRejectionFeedback?: boolean;
    }) => {
      return rpcClient.applications.move({
        id: params.applicationId,
        targetColumn: params.targetColumn,
        targetInterviewStageId: params.targetInterviewStageId,
        currentStepOwnerId: params.currentStepOwnerId || undefined,
        forceMove: params.forceMove || false,
        createNewRecord: params.createNewRecord || false,
        rejectionReason: params.rejectionReason || undefined,
        rejectionFeedback: params.rejectionFeedback || undefined,
        rejectionFeedbackSubject: params.rejectionFeedbackSubject || undefined,
        sendRejectionFeedback: params.sendRejectionFeedback || false,
      });
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        jobId
          ? queryClient.refetchQueries({
              queryKey: orpc.applications.getJobKanban.queryOptions({ jobOpeningId: jobId })
                .queryKey,
            })
          : Promise.resolve(),
        queryClient.refetchQueries({
          queryKey: orpc.applications.getDetails.queryOptions({ id: variables.applicationId })
            .queryKey,
        }),
      ]);
      queryClient.invalidateQueries({
        queryKey: orpc.applications.list.queryOptions().queryKey,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: orpc.interviews.mine.queryOptions().queryKey,
        refetchType: "all",
      });
      if (additionalInvalidateKeys) {
        additionalInvalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key, refetchType: "all" });
        });
      }
      toast({
        title: "Candidato movido",
        description: "O status do candidato foi atualizado.",
      });
      onSuccess?.();
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível mover o candidato.",
        variant: "destructive",
      });
    },
  });

  const hireMutation = useMutation({
    mutationFn: async (params: {
      applicationId: string;
      fullName: string;
      personalEmail?: string;
      phone?: string;
      employmentType?: "CLT" | "PJ";
      jobTitle?: string;
      jobLevel?: string;
      department?: string;
      startDate?: string;
      initialSalary?: string;
    }) => {
      return rpcClient.applications.hireAndCreateEmployee({
        id: params.applicationId,
        fullName: params.fullName,
        personalEmail: params.personalEmail || undefined,
        phone: params.phone || undefined,
        employmentType: params.employmentType,
        jobTitle: params.jobTitle || undefined,
        jobLevel: params.jobLevel || undefined,
        department: params.department || undefined,
        startDate: params.startDate || undefined,
        initialSalary: params.initialSalary || undefined,
      });
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        jobId
          ? queryClient.refetchQueries({
              queryKey: orpc.applications.getJobKanban.queryOptions({ jobOpeningId: jobId })
                .queryKey,
            })
          : Promise.resolve(),
        queryClient.refetchQueries({
          queryKey: orpc.applications.getDetails.queryOptions({ id: variables.applicationId })
            .queryKey,
        }),
      ]);
      queryClient.invalidateQueries({
        queryKey: orpc.applications.list.queryOptions().queryKey,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: orpc.employees.list.queryOptions().queryKey,
        refetchType: "all",
      });
      if (additionalInvalidateKeys) {
        additionalInvalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key, refetchType: "all" });
        });
      }
      toast({
        title: "Candidato contratado",
        description: "O candidato foi movido para Contratado e o colaborador foi criado.",
      });
      onSuccess?.();
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível contratar o candidato.",
        variant: "destructive",
      });
    },
  });

  const resetMoveState = () => {
    setIsConfirmOpen(false);
    setPendingMove(null);
    setInterviewerId("");
    setSelectedGroupId(null);
    setCurrentStepOwnerId("");
    setCreateNewRecord(false);
    setSendEmailToCandidate(true);
    setEmailStageName("");
    setEmailSubject("");
    setEmailIntroText("");
    setEmailExtraBody("");
    setEmailButtonText("");
    setEmailClosingText("");
    setAssignmentMode("automatic");
    setRejectionReason("");
    setSendRejectionFeedback(false);
    setRejectionFeedbackSubject("");
    setRejectionFeedbackBody("");
    setShowRejectionPreview(false);
    setHireFullName("");
    setHirePersonalEmail("");
    setHirePhone("");
    setHireEmploymentType("PJ");
    setHireJobTitle("");
    setHireJobLevel("");
    setHireDepartment("");
    setHireStartDate("");
    setHireInitialSalary("");
  };

  const cancelMove = () => {
    resetMoveState();
  };

  const buildOptions = (stageId: string | null): AssignmentOption[] =>
    buildAssignmentOptions(stageInterviewers, stageId);

  const initMove = async (params: InitMoveParams) => {
    const {
      applicationId,
      targetColumn,
      targetInterviewStageId,
      applicantName,
      targetColumnTitle,
      isInterviewStage,
      stage,
    } = params;

    const assignmentOptions = buildOptions(targetInterviewStageId);
    const hasStageInterviewers = assignmentOptions.length > 0;

    // Set owner for reference_checks
    const isOwnerStage = targetColumn === "reference_checks";
    if (isOwnerStage && hiringManagerId) {
      setCurrentStepOwnerId(hiringManagerId);
    } else {
      setCurrentStepOwnerId("");
    }

    // Set interviewer assignment
    if (isInterviewStage && hasStageInterviewers) {
      setAssignmentMode("automatic");
      const best = assignmentOptions.reduce(
        (min, curr) => (curr.interviewCount < min.interviewCount ? curr : min),
        assignmentOptions[0],
      );
      if (best.type === "group") {
        setSelectedGroupId(best.id);
        setInterviewerId("");
      } else {
        setSelectedGroupId(null);
        setInterviewerId(best.id);
      }
    } else if (isInterviewStage && stage?.defaultInterviewerId) {
      setAssignmentMode("manual");
      setSelectedGroupId(null);
      setInterviewerId(stage.defaultInterviewerId);
    } else {
      setAssignmentMode("manual");
      setSelectedGroupId(null);
      setInterviewerId("");
    }

    const isOfferTarget = targetColumn === "offer";

    // Validate move
    setIsValidating(true);
    try {
      const validationData = await rpcClient.applications.validateMove({
        id: applicationId,
        targetColumn,
        targetInterviewStageId,
      });

      // Non-authorized offer move: skip dialog and move directly, bypassing any confirmation
      if (isOfferTarget && !canCreateOffer) {
        const bypassConfirmation = validationData.validation?.requiresConfirmation ?? false;
        moveMutation.mutate({
          applicationId,
          targetColumn,
          targetInterviewStageId,
          forceMove: bypassConfirmation,
          createNewRecord: false,
        });
        return;
      }

      const hireFormNeeded = validationData.requiresHireForm ?? false;
      if (hireFormNeeded) {
        setHireFullName(applicantName);
        setHireJobTitle(jobTitle);
        if (jobLevelOption) setHireJobLevel(jobLevelOption);
        if (jobDepartment) setHireDepartment(jobDepartment);
      }

      setPendingMove({
        applicationId,
        targetColumn,
        targetInterviewStageId,
        applicantName,
        targetColumnTitle,
        isInterviewStage,
        stage,
        validation: validationData.validation,
        requiresHireForm: hireFormNeeded,
      });
      setCreateNewRecord(false);
      setIsConfirmOpen(true);
    } catch {
      // Non-authorized offer move: skip dialog and move directly
      if (isOfferTarget && !canCreateOffer) {
        moveMutation.mutate({
          applicationId,
          targetColumn,
          targetInterviewStageId,
          createNewRecord: false,
        });
        return;
      }

      const isHireTarget = targetColumn === "hired";
      if (isHireTarget) {
        setHireFullName(applicantName);
        setHireJobTitle(jobTitle);
        if (jobLevelOption) setHireJobLevel(jobLevelOption);
        if (jobDepartment) setHireDepartment(jobDepartment);
      }

      setPendingMove({
        applicationId,
        targetColumn,
        targetInterviewStageId,
        applicantName,
        targetColumnTitle,
        isInterviewStage,
        stage,
        requiresHireForm: isHireTarget,
      });
      setIsConfirmOpen(true);
    } finally {
      setIsValidating(false);
    }
  };

  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);

  const confirmMove = async () => {
    if (!pendingMove || isConfirmSubmitting) return;
    setIsConfirmSubmitting(true);
    try {
      await _confirmMoveInner();
    } finally {
      setIsConfirmSubmitting(false);
    }
  };

  const _confirmMoveInner = async () => {
    if (!pendingMove) return;

    const isOfferTarget = pendingMove.targetColumn === "offer";
    const isRejectionTarget = pendingMove.targetColumn === "rejected";
    const hasWarnings = pendingMove.validation?.requiresConfirmation;

    const shouldCreateOffer = isOfferTarget && needsOfferForm;

    if (isRejectionTarget) {
      try {
        await moveMutation.mutateAsync({
          applicationId: pendingMove.applicationId,
          targetColumn: pendingMove.targetColumn,
          targetInterviewStageId: pendingMove.targetInterviewStageId,
          forceMove: hasWarnings,
          createNewRecord: false,
          rejectionReason,
          rejectionFeedback: sendRejectionFeedback ? rejectionFeedbackBody : undefined,
          rejectionFeedbackSubject: sendRejectionFeedback ? rejectionFeedbackSubject : undefined,
          sendRejectionFeedback,
        });
        if (sendRejectionFeedback) {
          toast({
            title: "Email de feedback enviado",
            description: "O candidato receberá o email de feedback.",
          });
        }
      } catch {
        resetMoveState();
        return;
      }
      resetMoveState();
      return;
    }

    if (pendingMove.requiresHireForm) {
      try {
        await hireMutation.mutateAsync({
          applicationId: pendingMove.applicationId,
          fullName: hireFullName,
          personalEmail: hirePersonalEmail || undefined,
          phone: hirePhone || undefined,
          employmentType: hireEmploymentType || undefined,
          jobTitle: hireJobTitle || undefined,
          jobLevel: hireJobLevel || undefined,
          department: hireDepartment || undefined,
          startDate: hireStartDate || undefined,
          initialSalary: hireInitialSalary || undefined,
        });
      } catch {
        resetMoveState();
        return;
      }
      resetMoveState();
      return;
    }

    if (shouldCreateOffer) {
      setOfferDialogApplicationId(pendingMove.applicationId);
      setOfferDialogApplicantName(pendingMove.applicantName);
      setIsCreateOfferDialogOpen(true);
      setIsConfirmOpen(false);
      return;
    } else if (isOfferTarget) {
      moveMutation.mutate({
        applicationId: pendingMove.applicationId,
        targetColumn: pendingMove.targetColumn,
        targetInterviewStageId: pendingMove.targetInterviewStageId,
        currentStepOwnerId: currentStepOwnerId || undefined,
        forceMove: hasWarnings,
        createNewRecord: false,
      });
    } else if (!isOfferTarget) {
      try {
        await moveMutation.mutateAsync({
          applicationId: pendingMove.applicationId,
          targetColumn: pendingMove.targetColumn,
          targetInterviewStageId: pendingMove.targetInterviewStageId,
          currentStepOwnerId: currentStepOwnerId || undefined,
          forceMove: hasWarnings,
          createNewRecord: createNewRecord,
        });
      } catch {
        resetMoveState();
        return;
      }

      if (
        pendingMove.isInterviewStage &&
        (interviewerId || selectedGroupId) &&
        (!hasWarnings || createNewRecord)
      ) {
        try {
          const interviewPayload: Record<string, any> = {
            applicationId: pendingMove.applicationId,
            interviewStageId: pendingMove.targetInterviewStageId,
            sendEmail: sendEmailToCandidate,
            force: true,
            emailStageNameOverride: emailStageName || undefined,
            emailSubjectOverride: emailSubject || undefined,
            emailIntroOverride: emailIntroText || undefined,
            emailExtraBody: emailExtraBody || undefined,
            emailButtonTextOverride: emailButtonText || undefined,
            emailClosingOverride: emailClosingText || undefined,
          };
          if (selectedGroupId) {
            interviewPayload.interviewerGroupId = selectedGroupId;
          } else {
            interviewPayload.interviewerId = interviewerId;
          }
          await rpcClient.interviews.create(interviewPayload);
          // Refetch kanban so the new interview's contextStatus is visible
          if (jobId) {
            await queryClient.refetchQueries({
              queryKey: orpc.applications.getJobKanban.queryOptions({ jobOpeningId: jobId })
                .queryKey,
            });
          }
          toast({
            title: "Entrevista criada",
            description: sendEmailToCandidate
              ? "O candidato receberá um email para agendar a entrevista."
              : "Entrevista registrada. O email não foi enviado ao candidato.",
          });
        } catch {
          toast({
            title: "Erro",
            description: "Não foi possível criar a entrevista.",
            variant: "destructive",
          });
        }
      }
    }

    resetMoveState();
  };

  // Derived state
  const canCreateOffer = canManage;
  const isOfferMove = pendingMove?.targetColumn === "offer";
  const isRejectionMove = pendingMove?.targetColumn === "rejected";
  const isHireMove = pendingMove?.requiresHireForm === true;
  const needsOfferForm =
    isOfferMove &&
    canCreateOffer &&
    (!pendingMove?.validation?.requiresConfirmation || createNewRecord);
  const needsInterviewer =
    pendingMove?.isInterviewStage &&
    (!pendingMove?.validation?.requiresConfirmation || createNewRecord);
  const hasValidInterviewer =
    !needsInterviewer || (interviewerId && interviewerId.length > 0) || !!selectedGroupId;
  const isStageInterviewersReady = !needsInterviewer || !isLoadingStageInterviewers;
  const canConfirmRejection =
    !isRejectionMove ||
    !sendRejectionFeedback ||
    (rejectionFeedbackSubject.trim().length > 0 && rejectionFeedbackBody.trim().length > 0);
  const canConfirmHire =
    !isHireMove ||
    (hireFullName.trim().length > 0 &&
      hirePersonalEmail.trim().length > 0 &&
      hirePhone.trim().length > 0 &&
      hireJobTitle.trim().length > 0 &&
      hireJobLevel.trim().length > 0 &&
      hireDepartment.trim().length > 0 &&
      hireEmploymentType !== "" &&
      hireInitialSalary.trim().length > 0);
  const canConfirmMove =
    hasValidInterviewer && isStageInterviewersReady && canConfirmRejection && canConfirmHire;

  // Resolve hiring manager name from people if not provided
  const resolvedHiringManagerFullName =
    hiringManagerFullName ||
    (hiringManagerId ? people?.find((p) => p.id === hiringManagerId)?.fullName : undefined) ||
    "";

  return {
    // State
    pendingMove,
    isConfirmOpen,
    isValidating,
    isConfirmSubmitting,
    interviewerId,
    setInterviewerId,
    selectedGroupId,
    setSelectedGroupId,
    assignmentMode,
    setAssignmentMode,
    createNewRecord,
    setCreateNewRecord,
    sendEmailToCandidate,
    setSendEmailToCandidate,
    emailStageName,
    setEmailStageName,
    emailSubject,
    setEmailSubject,
    emailIntroText,
    setEmailIntroText,
    emailExtraBody,
    setEmailExtraBody,
    emailButtonText,
    setEmailButtonText,
    emailClosingText,
    setEmailClosingText,
    currentStepOwnerId,
    setCurrentStepOwnerId,
    rejectionReason,
    setRejectionReason,
    sendRejectionFeedback,
    setSendRejectionFeedback,
    rejectionFeedbackSubject,
    setRejectionFeedbackSubject,
    rejectionFeedbackBody,
    setRejectionFeedbackBody,
    showRejectionPreview,
    setShowRejectionPreview,
    isCreateOfferDialogOpen,
    setIsCreateOfferDialogOpen,
    offerDialogApplicationId,
    offerDialogApplicantName,

    // Hire form state
    hireFullName,
    setHireFullName,
    hirePersonalEmail,
    setHirePersonalEmail,
    hirePhone,
    setHirePhone,
    hireEmploymentType,
    setHireEmploymentType,
    hireJobTitle,
    setHireJobTitle,
    hireJobLevel,
    setHireJobLevel,
    hireDepartment,
    setHireDepartment,
    hireStartDate,
    setHireStartDate,
    hireInitialSalary,
    setHireInitialSalary,

    // Derived
    isOfferMove,
    isRejectionMove,
    isHireMove,
    needsOfferForm,
    needsInterviewer,
    hasValidInterviewer,
    canConfirmMove,
    resolvedHiringManagerFullName,

    // Actions
    initMove,
    confirmMove,
    cancelMove,
    resetMoveState,
    moveMutation,
    buildAssignmentOptions: buildOptions,
  };
}

export type UseMoveApplicationReturn = ReturnType<typeof useMoveApplication>;
